import { useDisclosure } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useState } from 'react';
import {
  Dropdown,
  DropdownContent,
  DropdownDivider,
  IconButton,
  useSnackbar,
} from 'ui-components';
import { MoreIcon } from 'ui-components/dist/MoreIcon';
import { SavedList } from '~/clientApi/savedLists.model';
import {
  useCreateList,
  useUpdateListCreators,
} from '~/clientApi/whalarModeLists';
import { SNACKBAR_ERROR_GENERIC_OPTIONS } from '~/constants';
import { routes } from '~/core/router';
import { useFetchCreator } from '~/hooks/api/useFetchCreator';
import { AddToListDropdownItem } from '~/screens/WhalarModeListDetailScreen/components/ListCreatorDropdownMenu/AddToListDropdownItem';
import EditListModal from '~/screens/WhalarModeListsScreen/components/EditListModal';
import { AddToListModal } from '~/screens/WhalarModeTalentRecord/components/AddToListModal';
import { useCreatorCopyEmbed } from '~/screens/WhalarModeTalentRecord/hooks/useCreatorCopyEmbed';

import { ManagerDropdown } from '../ManagerDropdown';
import { CopyEmbedDropdownItem } from './CopyEmbedDropdownItem';
import { ITalentDropdownMenu } from './TalentDropdownMenu.types';

export const TalentDropdownMenu: React.FC<ITalentDropdownMenu> = ({
  creator,
}) => {
  const snackbar = useSnackbar();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const router = useRouter();
  const {
    isOpen: isAddCreatorToListModalOpen,
    onClose: onAddCreatorToListModalClose,
    onOpen: onAddCreatorToListModalOpen,
  } = useDisclosure();

  const {
    isOpen: isOpenCreateList,
    onOpen: onOpenCreateList,
    onClose: onCloseCreateList,
  } = useDisclosure();

  const { mutateAsync: mutateAsyncCreators } = useUpdateListCreators();
  const creationMutation = useCreateList();

  /* 
    Fetches manager name and email if not available in `manager`. This provides compatibility with the creators returned from Search API
    Also, api-search doesn't return the correct YT channelId, this is neecesary to get YT insights. 
  */
  const { data: { manager, ytChannels } = {} } = useFetchCreator(
    isDropdownOpen ? creator?.id : undefined,
  ); // Do not fetch if the dropdown is closed

  const { onBasicClick, onDetailClick } = useCreatorCopyEmbed({
    ...creator,
    ytChannels,
  });

  const handleAddToList = async (list: SavedList) => {
    try {
      await mutateAsyncCreators({ id: list.id, addCreatorIds: [creator?.id] });
      snackbar.success({
        description: 'List updated: The list has been updated',
        duration: 9000,
        isCloseable: true,
      });
      router.push(routes.listDetail(list.id));
    } catch {
      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
    }
  };

  const handleCreateList = ({
    listName,
  }: {
    id?: string;
    listName: string;
  }) => {
    creationMutation.mutate(
      { name: listName },
      {
        onSuccess: async (result) => {
          await handleAddToList(result.data);
        },
      },
    );
  };

  return (
    <>
      {/* TODO: This two modals should be moved to a shared place instead of getting from the lists screen */}
      <AddToListModal
        creators={[creator]}
        isOpen={isAddCreatorToListModalOpen}
        onClose={onAddCreatorToListModalClose}
        onItemClick={handleAddToList}
        onCreateListOpen={onOpenCreateList}
      />
      <EditListModal
        isOpen={isOpenCreateList}
        onClose={onCloseCreateList}
        onEdit={handleCreateList}
      />

      <Dropdown>
        {({ isOpen, buttonRef, getReferenceProps }) => (
          <>
            <IconButton
              aria-label="Open creator invitation actions"
              icon={<MoreIcon boxSize={4} />}
              isActive={isOpen}
              ref={buttonRef}
              size="md"
              variant="transparent"
              {...getReferenceProps()}
              onClick={() => setIsDropdownOpen(isOpen ?? false)}
            />
            <DropdownContent
              __css={{
                // Required to fix my agency header z-index issue
                zIndex: 1099,
              }}
            >
              <CopyEmbedDropdownItem
                creator={creator}
                onBasicCopy={onBasicClick}
                onDetailCopy={onDetailClick}
              />
              <DropdownDivider />

              <AddToListDropdownItem onClick={onAddCreatorToListModalOpen} />

              <ManagerDropdown
                manager={manager}
                title="Talent Manager"
                isShared={false}
              />
            </DropdownContent>
          </>
        )}
      </Dropdown>
    </>
  );
};
